/**
 * Eric 2026-06-25 (iter121 — Session A3) — Panneau de gestion des locuteurs
 * détectés (diarisation AssemblyAI).
 *
 * Extrait de VideoDetailPage.js dans le cadre du refacto P0
 * (5620 → cible <1500 lignes).
 *
 * Permet :
 *   - Voir les locuteurs détectés + leur photo (si profil mappé).
 *   - Remapper chaque locuteur détecté vers un profil connu via <select>.
 *   - Raccourci « un seul locuteur » : assigne tous les segments à un seul
 *     profil (utile quand la diarisation a sur-segmenté à tort).
 *   - Valider l'identification (propage dans la transcription).
 *   - Relancer la diarisation AssemblyAI.
 *
 * Vincent #355 (Eric 2026-06-22) — Tous les contrôles d'édition (selects
 * actifs, boutons Valider/Ré-analyser, section « un seul locuteur ») sont
 * gated par `canEdit` côté parent. Lecture seule sinon.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog, promptDialog } from "../../lib/uiDialogs";
import { SpinnerGap, Microphone, SpeakerHigh, CheckCircle, ArrowClockwise, EnvelopeSimple } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import NonDiariseeBanner from "./NonDiariseeBanner"; // Lot B2

export const SpeakersPanel = ({
  show,
  canEdit,
  lang,
  api,
  speakerData,
  speakerMapping,
  setSpeakerMapping,
  speakerSaving,
  speakerReprocessing,
  handleValidateSpeakers,
  handleReprocessSpeakers,
  // Vincent XLSX 2026-07-15 Rows 473 + 478 + 481 + 487 (Eric 2026-07-16
  // SPEC STRICK 2e passe) — Nouveaux props pour la liste à cocher des
  // locuteurs présents + toggles bypass diarisation/facial.
  presentSpeakers,
  setPresentSpeakers,
  useDiarisation,
  setUseDiarisation,
  useFacialId,
  setUseFacialId,
  // Eric 2026-07-20 (iter162) — Ticket #500 debug — bouton "Copier bundle debug"
  // visible fondateur only pour extraire logs+doc video pour investigation.
  isFounder,
  isAdmin,
  videoId,
  video,
  loadVideoData,
}) => {
  // Vincent (2026-02) — Nouveaux états pour boutons « Relancer pipeline »
  // et « Ré-optimiser » (visible pour toutes les vidéos, pas seulement
  // celles avec codec_non_standard). Cible : bouton pour « corriger » une
  // vidéo directement depuis l'onglet Locuteurs.
  const [reprocessingPipeline, setReprocessingPipeline] = useState(false);
  const [reoptimizingVideo, setReoptimizingVideo] = useState(false);

  const handleReprocessPipeline = async () => {
    if (!videoId) return;
    const ok = await confirmDialog(
      lang === "fr"
        ? "Relancer TOUT le pipeline (transcription + diarisation + extraction) depuis le début ? Cela remplacera la transcription et les connaissances actuelles."
        : "Restart the ENTIRE pipeline (transcription + diarization + extraction) from scratch? This will replace the current transcript and knowledge.",
      { danger: true, confirmLabel: lang === "fr" ? "Relancer TOUT" : "Restart ALL" },
    );
    if (!ok) return;
    setReprocessingPipeline(true);
    try {
      // Vincent B612 récurrence (Eric 2026-07-30 SPEC STRICK) — Envoie l'état
      // ACTUEL des cases Diarisation/ID faciale (comme /rerun-whisper) pour
      // que le backend les persiste sur le doc video AVANT de relancer le
      // pipeline. Sans cela, les couleurs des étapes correspondantes dans
      // la barre pipeline (rouge=off / jaune=en cours / vert=actif) ne
      // reflétaient pas le nouveau réglage.
      await axios.post(
        `${api}/api/videos/${videoId}/reprocess`,
        {
          use_diarisation: useDiarisation,
          use_facial_id: useFacialId,
        },
        { withCredentials: true }
      );
      toast.success(lang === "fr" ? "Pipeline relancé — traitement en cours." : "Pipeline restarted — processing.");
      if (typeof loadVideoData === "function") loadVideoData();
    } catch (e) {
      console.error("[reprocessPipeline]", e);
      toast.error(
        (lang === "fr" ? "Échec du relancement : " : "Restart failed: ") +
        (e?.response?.data?.detail || e?.message || "erreur inconnue")
      );
    } finally {
      setReprocessingPipeline(false);
    }
  };

  const handleReoptimizeVideo = async () => {
    if (!videoId) return;
    const ok = await confirmDialog(
      lang === "fr"
        ? "Ré-encoder le fichier en H.264/yuv420p ? Utile si le player affiche un écran noir (codec incompatible)."
        : "Re-encode the file to H.264/yuv420p? Useful when the player shows a black screen (incompatible codec).",
      { confirmLabel: lang === "fr" ? "Ré-encoder" : "Re-encode", tone: "warning" },
    );
    if (!ok) return;
    setReoptimizingVideo(true);
    try {
      await axios.post(`${api}/api/videos/${videoId}/optimize`, {}, { withCredentials: true });
      toast.success(lang === "fr" ? "Ré-optimisation FFmpeg lancée." : "FFmpeg re-optimization started.");
      if (typeof loadVideoData === "function") loadVideoData();
    } catch (e) {
      console.error("[reoptimizeVideo]", e);
      toast.error(
        (lang === "fr" ? "Échec de la ré-optimisation : " : "Re-optimization failed: ") +
        (e?.response?.data?.detail || e?.message || "erreur inconnue")
      );
    } finally {
      setReoptimizingVideo(false);
    }
  };
  // Vincent Row 481 + XLSX 2026-07-20 Row 504 sous-point 1 (Eric SPEC STRICK) —
  // Pré-remplissage automatique multi-source :
  //   1. preselected_speaker_names : locuteurs cochés à l'UPLOAD (via mode
  //      multi_select ou single) — nouvelle source #504
  //   2. fireflies_speakers : noms extraits de la transcription Fireflies
  //      (Row 481 original)
  // Priorité : upload > fireflies (l'utilisateur a fait un choix explicite
  // au moment de l'upload, qui a plus de poids que l'inférence Fireflies).
  useEffect(() => {
    if (!show) return;
    if (!speakerData || !setPresentSpeakers) return;
    if (!presentSpeakers || presentSpeakers.size > 0) return;
    const known = speakerData.known_speakers || [];
    if (known.length === 0) return;
    const preSel = new Set();
    // Source 1 : preselected à l'upload (par nom)
    for (const name of speakerData.preselected_speaker_names || []) {
      if (name) preSel.add(name);
    }
    // Source 2 : Fireflies (fallback si upload ne s'est rien passé)
    if (preSel.size === 0) {
      for (const ff of speakerData.fireflies_speakers || []) {
        const ffName = String(ff?.name || ff || "").trim();
        if (!ffName || /^speaker\s*\d+$/i.test(ffName)) continue;
        const match = known.find(
          (k) => k.name && String(k.name).trim().toLowerCase() === ffName.toLowerCase()
        );
        if (match) preSel.add(match.name);
      }
    }
    if (preSel.size > 0) {
      setPresentSpeakers(preSel);
    }
  }, [show, speakerData, presentSpeakers, setPresentSpeakers]);

  if (!show) return null;

  const togglePresent = (name) => {
    if (!setPresentSpeakers) return;
    setPresentSpeakers((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  return (
    <div
      className="max-h-[calc(100vh-280px)] min-h-[300px] overflow-y-auto p-4 space-y-4"
      data-testid="speakers-panel"
    >
      {!speakerData ? (
        <div className="flex justify-center py-10">
          <SpinnerGap size={24} className="animate-spin text-zinc-400" />
        </div>
      ) : (
        <>
          {/* Lot B2 (Eric 2026-06-30) — Pourquoi un seul locuteur ? Une vidéo
              non diarisée ressemblait à une vidéo où une seule personne parle.
              Le motif est lu sur les segments (`diarization_source`). */}
          <NonDiariseeBanner segments={speakerData.segments} lang={lang} />
          {/* Vincent (2026-02) — Bandeau info audio-only : le fichier étant
              un .mp3/.wav/etc., le player affiche un rectangle noir (pas de
              piste vidéo). Cela évite la confusion « BOGUE vidéo noire »
              alors que le comportement est normal. */}
          {video?.is_audio_only && (
            <div
              className="p-3 bg-sky-50 border border-sky-300 rounded-lg flex items-start gap-3"
              data-testid="audio-only-info-banner"
            >
              <SpeakerHigh size={18} className="text-sky-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-sky-800">
                  {lang === "fr" ? "Fichier audio uniquement" : "Audio-only file"}
                </p>
                <p className="text-xs text-sky-700 mt-0.5">
                  {lang === "fr"
                    ? "Ce fichier ne contient PAS de piste vidéo (audio seul). L'écran noir du player est normal — seul le son est disponible."
                    : "This file has NO video track (audio only). The black screen in the player is normal — only sound is available."}
                </p>
              </div>
            </div>
          )}
          {/* Vincent XLSX 2026-07-15 11h30 Row 474+478 (Eric 2026-07-15 SPEC
              STRICK) — Bannière PERSISTANTE visible pendant la ré-analyse
              audio pour indiquer clairement que le pipeline travaille. */}
          {speakerReprocessing && (
            <div
              className="p-3 bg-blue-50 border border-blue-300 rounded-lg flex items-start gap-3 animate-pulse"
              data-testid="speakers-reanalyze-banner"
            >
              <SpinnerGap size={20} className="animate-spin text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-blue-800">
                  {"🎙️ Ré-analyse de l'audio en cours…"}
                </p>
                <p className="text-xs text-blue-700 mt-0.5">
                  {"Étapes : (1) diarisation AssemblyAI (identification des tours de parole), (2) reconnaissance visuelle des visages, (3) association aux profils de locuteurs connus. Cela peut prendre 30 secondes à plusieurs minutes selon la durée de la vidéo. Ne fermez pas cet onglet."}
                </p>
              </div>
            </div>
          )}
          {/* Vincent XLSX 2026-07-15 Rows 473 + 481 + 487 (Eric 2026-07-16
              SPEC STRICK 2e passe) — LISTE À COCHER (multi-sélection) de tous
              les locuteurs connus. Cette liste indique EXPLICITEMENT quels
              locuteurs sont présents dans la vidéo → contrainte pour la
              ré-identification (envoyée dans le body de /speakers/reprocess).
              Ligne pré-remplie automatiquement depuis Fireflies si les prénoms
              matchent la liste des locuteurs connus (Row 481).
              La liste est PERSISTANTE : reste visible pendant la ré-analyse. */}
          {canEdit && (speakerData.known_speakers?.length || 0) > 0 && (
            <div
              className="p-3 bg-white border border-zinc-200 rounded-lg space-y-2"
              data-testid="speakers-present-checklist"
            >
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-zinc-800">
                  {lang === "fr"
                    ? "Locuteurs présents dans cette vidéo (multi-sélection)"
                    : "Speakers present in this video (multi-select)"}
                </p>
                <span className="text-[10px] text-zinc-500" data-testid="speakers-present-count">
                  {presentSpeakers ? presentSpeakers.size : 0} / {speakerData.known_speakers.length}
                </span>
              </div>
              <p className="text-[10px] text-zinc-500 leading-snug">
                {lang === "fr"
                  ? "Cochez tous les locuteurs qui parlent dans cette vidéo. Ces choix seront utilisés comme indice lors de la ré-analyse audio (bouton ci-dessous)."
                  : "Check every speaker who appears in this video. Used as a hint when re-analyzing the audio below."}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-56 overflow-y-auto pr-1">
                {[...(speakerData.known_speakers || [])]
                  .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "fr", { sensitivity: "base" }))
                  .map((ks) => {
                    const checked = !!(presentSpeakers && presentSpeakers.has(ks.name));
                    // Vincent B716 (Eric 2026-02-11) — Si le locuteur est
                    // associé à un utilisateur plateforme, le NOM devient
                    // un bouton qui ouvre une conversation individuelle
                    // avec cet utilisateur (route /dashboard/agora avec
                    // ?dm=<user_id>). Le reste de la ligne (checkbox +
                    // label vide) reste cliquable pour toggler la présence.
                    const linkedUser = ks.associated_user;
                    return (
                      <label
                        key={ks.id}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs transition-colors ${
                          checked ? "bg-teal-50 border border-teal-300" : "hover:bg-zinc-50 border border-transparent"
                        }`}
                        data-testid={`speakers-present-row-${ks.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePresent(ks.name)}
                          className="h-3.5 w-3.5 accent-emerald-600"
                          data-testid={`speakers-present-check-${ks.id}`}
                        />
                        <span className="truncate flex items-center gap-1">
                          {linkedUser ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // Route vers messagerie → onglet Conversations → auto-DM
                                if (typeof window !== "undefined") {
                                  window.location.href = `/dashboard/agora?tab=conversations&dm=${encodeURIComponent(linkedUser.id)}`;
                                }
                              }}
                              className="font-bold text-[#002FA7] hover:underline focus:outline-none focus:ring-1 focus:ring-[#002FA7] rounded"
                              title={lang === "fr"
                                ? `Ouvrir une conversation avec ${linkedUser.name}`
                                : `Message ${linkedUser.name}`}
                              data-testid={`speaker-dm-btn-${ks.id}`}
                            >
                              {ks.name}
                            </button>
                          ) : (
                            <strong>{ks.name}</strong>
                          )}
                          {ks.role && <span className="text-zinc-500 ml-1">({ks.role})</span>}
                          {linkedUser && (
                            <span
                              className="text-[9px] text-violet-600 bg-violet-500/10 rounded-full px-1.5 py-0.5 ml-1"
                              title={linkedUser.email}
                              aria-label="Utilisateur plateforme associé"
                            >
                              ✉
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
              </div>
              {/* Vincent Row 487 — Toggles Diarisation / Identification faciale
                  (bypass) AU-DESSUS du bouton « Ré-analyser », comme lors de
                  l'upload initial.
                  Vincent B570 récurrence (Eric 2026-08-05 SPEC STRICK PROD) —
                  Deux règles per-vidéo à faire respecter STRICTEMENT :
                  1) Configurations autorisées :
                     (on, on), (on, off), (off, off). Interdit : (off, on).
                     → Si l'utilisateur met Diarisation OFF alors que
                       Identification faciale est ON, on force auto la 2e à OFF.
                     → Le toggle Identification faciale est disabled tant que
                       Diarisation est OFF (impossible de le remettre à ON).
                  2) Pendant que le pipeline analyse la transcription
                     (pipeline_status ∈ {queued, processing}), les 2 toggles
                     sont verrouillés. Ils redeviennent modifiables dès que
                     le pipeline pause pour la fusion (paused_pending_*),
                     se termine (complete) ou échoue (failed).
              */}
              {(() => {
                const _pipelineLocked = ["queued", "processing"].includes(video?.pipeline_status);
                const _facialLockedByDeps = !useDiarisation;
                const _diarDisabled = _pipelineLocked;
                const _facialDisabled = _pipelineLocked || _facialLockedByDeps;
                const _diarTitle = _pipelineLocked
                  ? (lang === "fr"
                      ? "Verrouillé — attends la fin de l'analyse (transcription Whisper + diarisation) avant de modifier ce réglage."
                      : "Locked — wait for the transcript analysis (Whisper + diarization) to finish before changing this.")
                  : (lang === "fr"
                      ? "Active/désactive la diarisation AssemblyAI pour cette vidéo (persisté à la prochaine relance du pipeline)."
                      : "Toggle AssemblyAI diarization for this video (persisted on next pipeline run).");
                const _facialTitle = _pipelineLocked
                  ? (lang === "fr"
                      ? "Verrouillé — attends la fin de l'analyse (transcription Whisper + diarisation) avant de modifier ce réglage."
                      : "Locked — wait for the transcript analysis to finish before changing this.")
                  : _facialLockedByDeps
                    ? (lang === "fr"
                        ? "Impossible d'activer l'Identification faciale sans la Diarisation : elle repose sur les locuteurs distingués par la Diarisation. Active d'abord la Diarisation."
                        : "Cannot enable Facial identification without Diarization: it relies on speakers separated by Diarization. Enable Diarization first.")
                    : (lang === "fr"
                        ? "Active/désactive l'identification faciale du locuteur pour cette vidéo."
                        : "Toggle facial identification of the speaker for this video.");
                const _onDiarChange = (e) => {
                  if (_pipelineLocked || !setUseDiarisation) return;
                  const next = e.target.checked;
                  setUseDiarisation(next);
                  // Vincent B570 récurrence — Force Identification faciale à OFF
                  // quand Diarisation passe à OFF (config (off,on) interdite).
                  if (!next && useFacialId && setUseFacialId) {
                    setUseFacialId(false);
                  }
                };
                const _onFacialChange = (e) => {
                  if (_pipelineLocked || !setUseFacialId) return;
                  const next = e.target.checked;
                  // Vincent B570 récurrence — Empêche mécaniquement d'activer
                  // Identification faciale tant que Diarisation est OFF.
                  if (next && !useDiarisation) {
                    toast.error(lang === "fr"
                      ? "Active d'abord la Diarisation : l'Identification faciale en dépend."
                      : "Enable Diarization first: Facial identification depends on it.");
                    return;
                  }
                  setUseFacialId(next);
                };
                return (
                  <>
                    <div
                      className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-zinc-100"
                      data-testid="toggles-diar-facial-container"
                      data-pipeline-locked={_pipelineLocked ? "true" : "false"}
                    >
                      <label
                        className={`flex items-center justify-between px-2 py-1.5 rounded border text-[11px] ${
                          _diarDisabled
                            ? "border-zinc-200 bg-zinc-100 cursor-not-allowed opacity-60"
                            : "border-zinc-200 bg-zinc-50 cursor-pointer"
                        }`}
                        data-testid="toggle-diarisation-row"
                        title={_diarTitle}
                      >
                        <span className="font-medium text-zinc-700">
                          {lang === "fr" ? "Diarisation" : "Diarization"}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <span className={`text-[10px] font-mono uppercase ${useDiarisation ? "text-teal-600" : "text-zinc-400"}`}>
                            {useDiarisation ? "ON" : "OFF"}
                          </span>
                          <input
                            type="checkbox"
                            checked={useDiarisation}
                            onChange={_onDiarChange}
                            disabled={_diarDisabled}
                            className="h-3.5 w-3.5 accent-emerald-600 disabled:cursor-not-allowed"
                            data-testid="toggle-diarisation"
                          />
                        </span>
                      </label>
                      <label
                        className={`flex items-center justify-between px-2 py-1.5 rounded border text-[11px] ${
                          _facialDisabled
                            ? "border-zinc-200 bg-zinc-100 cursor-not-allowed opacity-60"
                            : "border-zinc-200 bg-zinc-50 cursor-pointer"
                        }`}
                        data-testid="toggle-facial-id-row"
                        title={_facialTitle}
                      >
                        <span className="font-medium text-zinc-700">
                          {lang === "fr" ? "Identification faciale" : "Facial identification"}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <span className={`text-[10px] font-mono uppercase ${useFacialId ? "text-teal-600" : "text-zinc-400"}`}>
                            {useFacialId ? "ON" : "OFF"}
                          </span>
                          <input
                            type="checkbox"
                            checked={useFacialId}
                            onChange={_onFacialChange}
                            disabled={_facialDisabled}
                            className="h-3.5 w-3.5 accent-emerald-600 disabled:cursor-not-allowed"
                            data-testid="toggle-facial-id"
                          />
                        </span>
                      </label>
                    </div>
                    {_pipelineLocked && (
                      <p
                        className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 leading-snug"
                        data-testid="toggles-pipeline-locked-message"
                      >
                        {lang === "fr"
                          ? "🔒 Réglages verrouillés : attends la fin de l'analyse de la transcription (Whisper + diarisation) avant de les modifier. Ils redeviendront modifiables à la pause « en attente de fusion »."
                          : "🔒 Settings locked: wait for the transcript analysis (Whisper + diarization) to finish before changing them. They will be editable again at the « awaiting fusion » pause."}
                      </p>
                    )}
                  </>
                );
              })()}
              <p className="text-[10px] text-zinc-400 leading-snug">
                {lang === "fr"
                  ? "Diarisation OFF → tous les segments seront attribués à un seul locuteur (le premier coché). Identification faciale OFF → seule la diarisation audio AssemblyAI est utilisée."
                  : "Diarization OFF → single speaker for all segments. Facial ID OFF → audio-only AssemblyAI diarization."}
              </p>
            </div>
          )}

          {/* Vincent XLSX 2026-07-20 Row 504 sous-point 6 (Eric SPEC STRICK) —
              Vue comparative Whisper vs Fireflies pour permettre la fusion.
              Affichée uniquement si Fireflies est présent.
              B729 (Eric 2026-02-13) — Concepteurs uniquement : c'est un
              outil de validation avant fusion, sans intérêt pour un
              consommateur en lecture. */}
          {canEdit && speakerData.has_fireflies && (
            <details className="p-3 bg-white border border-zinc-200 rounded-lg" data-testid="transcript-compare-panel">
              <summary className="text-xs font-semibold text-zinc-800 cursor-pointer hover:text-zinc-900">
                {lang === "fr"
                  ? "🔀 Comparaison Whisper ↔ Fireflies (déplier pour validation avant fusion)"
                  : "🔀 Whisper vs Fireflies comparison (expand to validate before fusion)"}
              </summary>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                <div>
                  <p className="text-[10px] uppercase font-bold text-blue-700 mb-1" data-testid="compare-whisper-header">
                    Whisper (IA — {speakerData.segments?.length || 0} segments)
                  </p>
                  <div className="max-h-64 overflow-y-auto text-[11px] leading-relaxed p-2 bg-blue-50 rounded border border-blue-200 font-mono">
                    {(speakerData.segments || []).slice(0, 200).map((s) => (
                      <div key={s.id} className="mb-1">
                        <span className="text-blue-600 font-semibold">
                          [{Math.floor((s.start || 0) / 60)}:{String(Math.floor((s.start || 0) % 60)).padStart(2, "0")}] {s.speaker}:
                        </span>{" "}
                        <span className="text-zinc-800">{s.text}</span>
                      </div>
                    ))}
                    {(speakerData.segments?.length || 0) > 200 && (
                      <p className="text-[10px] italic text-blue-500 mt-2">
                        ({speakerData.segments.length - 200} segments supplémentaires — voir onglet Transcription)
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-purple-700 mb-1" data-testid="compare-fireflies-header">
                    Fireflies (humain — {speakerData.fireflies_speakers?.length || 0} locuteurs détectés)
                  </p>
                  <div className="max-h-64 overflow-y-auto text-[11px] leading-relaxed p-2 bg-purple-50 rounded border border-purple-200">
                    <p className="text-purple-900 whitespace-pre-wrap font-mono">
                      {/* Pour la vue compacte, on affiche les 5000 premiers caractères */}
                      {(() => {
                        const txt = (speakerData.fireflies_text || "").slice(0, 5000);
                        return txt || "(Fireflies transcript vide — vérifie l'import)";
                      })()}
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-zinc-500 mt-2 leading-snug">
                {lang === "fr"
                  ? "💡 Compare les 2 transcriptions. Si Fireflies contient de meilleurs noms de locuteurs, la fusion les préservera (voir #494)."
                  : "💡 Compare both transcripts. If Fireflies has better speaker names, fusion will preserve them."}
              </p>
            </details>
          )}

          {/* Detected speakers */}
          {/* Vincent XLSX 2026-07-15 11h30 Rows 46217 #1 + 46218 #2
              (Eric 2026-07-15 SPEC STRICK) — Panneau d'actions groupées :
              (a) bouton "Sélection multiple" pour appliquer un même locuteur
                  connu à plusieurs Speaker_X d'un coup ;
              (b) bouton "Pré-remplir depuis Fireflies" qui devine les
                  correspondances si les noms Fireflies matchent la liste
                  des locuteurs connus. */}
          {canEdit && (speakerData.detected_speakers?.length || 0) > 1 && (
            <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg space-y-2" data-testid="speakers-bulk-actions">
              <p className="text-xs font-semibold text-teal-800">
                {"Actions groupées"}
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    const knownNames = (speakerData.known_speakers || [])
                      .filter((k) => k.identity_verified || k.name)
                      .map((k) => k.name)
                      .filter(Boolean);
                    if (knownNames.length === 0) {
                      toast.info("Aucun locuteur vérifié à sélectionner.");
                      return;
                    }
                    // Vincent B658 (Eric 2026-08-05 SPEC STRICK PROD) — Migration
                    // window.prompt() → promptDialog() (lib/uiDialogs.js) : modal
                    // KNORIA cohérent, gestion clavier (Enter/Escape) et bouton
                    // annuler natif. Autorise Vincent à choisir 1 locuteur qu'il
                    // applique à TOUS les Speaker_X non encore assignés.
                    const choice = await promptDialog(
                      `Sélection multiple : à quel locuteur assigner tous les Speaker_X non identifiés ? Choix disponibles : ${knownNames.map((n, i) => `${i + 1}. ${n}`).join(" · ")}. Tape le numéro (1-${knownNames.length}) :`,
                      { placeholder: `1 à ${knownNames.length}`, confirmLabel: "Attribuer" },
                    );
                    if (choice === null) return;
                    const idx = parseInt(choice, 10) - 1;
                    if (isNaN(idx) || idx < 0 || idx >= knownNames.length) {
                      toast.error(`Numéro invalide. Attends un nombre entre 1 et ${knownNames.length}.`);
                      return;
                    }
                    const chosenName = knownNames[idx];
                    setSpeakerMapping((m) => {
                      const next = { ...m };
                      (speakerData.detected_speakers || []).forEach((spk) => {
                        if (!next[spk] || next[spk] === spk) {
                          next[spk] = chosenName;
                        }
                      });
                      return next;
                    });
                    toast.success(`Tous les locuteurs non identifiés → ${chosenName}`);
                  }}
                  className="text-[11px] px-2.5 py-1 rounded bg-teal-600 text-white hover:bg-teal-700 font-semibold"
                  data-testid="speakers-bulk-assign-btn"
                >
                  ✓ {"Sélection multiple"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Vincent B895 (Eric 2026-02-14 SPEC STRICK PROD) —
                    // Pré-remplissage depuis les noms Fireflies détectés.
                    // Correctif : match par NOM (pool), pas par index. Le
                    // positionnement `fireflies[i]` (i = index du détecté)
                    // était sémantiquement faux : rien ne garantit que le
                    // i-ème Speaker_X AssemblyAI correspond à la i-ème
                    // occurrence dans Fireflies.
                    const fireflies = speakerData.fireflies_speakers || [];
                    const known = speakerData.known_speakers || [];
                    if (fireflies.length === 0) {
                      toast.info("Aucun nom Fireflies détecté sur cette vidéo.");
                      return;
                    }
                    // Pool : { normalizedFFName → matched known_speaker.name }.
                    const norm = (s) => String(s || "").trim().toLowerCase();
                    const pool = [];
                    for (const ff of fireflies) {
                      const ffName = String(ff?.name || ff || "").trim();
                      if (!ffName) continue;
                      const kn = known.find((k) => k.name && norm(k.name) === norm(ffName));
                      if (kn) pool.push(kn.name);
                    }
                    if (pool.length === 0) {
                      toast.info(
                        `Fireflies liste ${fireflies.length} nom(s) mais aucun ne correspond aux locuteurs connus. Ajoutez-les à la banque puis relancez.`
                      );
                      return;
                    }
                    // Assigne chaque nom du pool à un Speaker_X non encore
                    // mappé (ordre stable : detected_speakers). On ne
                    // double pas les mêmes noms sur plusieurs détectés.
                    let matched = 0;
                    setSpeakerMapping((m) => {
                      const next = { ...m };
                      const already = new Set(Object.values(next).filter(Boolean));
                      for (const spk of (speakerData.detected_speakers || [])) {
                        if (next[spk] && next[spk] !== spk) continue; // déjà mappé
                        const candidate = pool.find((n) => !already.has(n));
                        if (!candidate) break;
                        next[spk] = candidate;
                        already.add(candidate);
                        matched++;
                      }
                      return next;
                    });
                    if (matched > 0) {
                      toast.success(`${matched} locuteur(s) pré-rempli(s) depuis Fireflies`);
                    } else {
                      toast.info("Tous les Speaker_X sont déjà mappés — rien à pré-remplir.");
                    }
                  }}
                  className="text-[11px] px-2.5 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 font-semibold"
                  data-testid="speakers-prefill-from-fireflies-btn"
                >
                  ⚡ {"Pré-remplir depuis Fireflies"}
                </button>
              </div>
            </div>
          )}

          <div>
            {/* Vincent B620 (Eric 2026-07-30 SPEC STRICK) — Le bouton
                « Valider l'identification » a été déplacé à droite du titre
                « Locuteurs détectés (N) » (voir plus haut dans le fichier).
                Le bouton « Ré-analyser l'audio » a été retiré (ne faisait
                pas ce qu'il fallait) — la relance de diarisation reste
                accessible via « Corriger la vidéo » plus bas.
                Vincent B625 (Eric 2026-07-30 SPEC STRICK PROD) — Feedback
                visuel de validation : après click sur « Valider », les
                lignes des locuteurs détectés passent en TEAL (fond
                teal-50 + bordure teal-400 + photo entourée teal), pour
                signaler clairement l'état validé. Le backend remet
                automatiquement `speaker_validation="pending"` lors d'une
                re-diarisation (voir routers/videos.py:5508) — les couleurs
                reviennent alors à l'état neutre initial (fond zinc-50). */}
            <div className="flex items-center justify-between gap-3 mb-2">
              <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider inline-flex items-center gap-2">
                {lang === "fr" ? "Locuteurs détectés" : "Detected speakers"} ({speakerData.detected_speakers?.length || 0})
                {/* Vincent B693 (Eric 2026-02-08) — Indicateur "N phrases lissées"
                    stocké dans video.diarization_stats.sentences_smoothed lors de
                    la diarisation. Affiché uniquement si >0. */}
                {video?.diarization_stats?.sentences_smoothed > 0 && (
                  <span
                    className="text-[10px] px-2 py-0.5 rounded-full border border-blue-200 bg-blue-50 text-blue-700 font-medium normal-case inline-flex items-center gap-1"
                    data-testid="smoothing-report"
                    title={
                      lang === "fr"
                        ? "Phrases dont le locuteur a été réajusté au dominant en temps de parole (post-processing B692)"
                        : "Sentences whose speaker was re-assigned to the time-dominant one"
                    }
                  >
                    📊 {video.diarization_stats.sentences_smoothed}{" "}
                    {lang === "fr" ? "phrases lissées" : "sentences smoothed"}
                  </span>
                )}
                {/* Vincent B733 v3 (Eric 2026-02-14) — Indicateur « N micro-tours
                    réattribués » stocké dans video.diarization_stats.micro_turns_split
                    lors du pre-pass B733 de la diarisation. Affiché uniquement
                    si >0. Prouve à Vincent/Étienne que le fix a bien opéré
                    sur cette vidéo précise. */}
                {video?.diarization_stats?.micro_turns_split > 0 && (
                  <span
                    className="text-[10px] px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 font-medium normal-case inline-flex items-center gap-1"
                    data-testid="micro-turns-split-report"
                    title={
                      lang === "fr"
                        ? "Micro-tours de parole préservés par le split B733 (relances courtes du minoritaire qui auraient été absorbées par le locuteur dominant)"
                        : "Micro-turns preserved by B733 split (short minority interjections that would have been absorbed by the dominant speaker)"
                    }
                  >
                    🎯 {video.diarization_stats.micro_turns_split}{" "}
                    {lang === "fr" ? "micro-tours réattribués" : "micro-turns re-attributed"}
                  </span>
                )}
                {/* Tâche 1184 (Eric 2026-06-25) — Indicateur « N bascules
                    réparées » : segments courts au recouvrement faible qui
                    sautaient d'un locuteur à l'autre et ont été ramenés au
                    locuteur qui les entoure. */}
                {video?.diarization_stats?.speaker_flips_repaired > 0 && (
                  <span
                    className="text-[10px] px-2 py-0.5 rounded-full border border-teal-200 bg-teal-50 text-teal-700 font-medium normal-case inline-flex items-center gap-1"
                    data-testid="speaker-flips-repaired-report"
                    title={
                      lang === "fr"
                        ? "Bascules isolées réparées (tâche 1184) : un segment court au recouvrement faible, coincé entre deux segments du même locuteur, lui a été rendu"
                        : "Isolated speaker flips repaired (task 1184)"
                    }
                  >
                    {video.diarization_stats.speaker_flips_repaired}{" "}
                    {lang === "fr" ? "bascules réparées" : "flips repaired"}
                  </span>
                )}
                {/* Vincent B635 récurrence (Eric 2026-08-05 SPEC STRICK PROD) —
                    Quand le pipeline tourne (queued/processing), le back-end a
                    remis `speaker_validation="pending"` mais le doc `video`
                    côté frontend peut encore afficher "validated" tant que
                    `loadVideoData()` n'a pas rafraîchi. On force donc la pill
                    « En cours » (ambre + spinner) au lieu de « Validé »
                    (vert + check) tant que la ré-analyse tourne. */}
                {(() => {
                  const _pipelineActive = ["queued", "processing"].includes(video?.pipeline_status);
                  if (_pipelineActive) {
                    return (
                      <span
                        className="text-[10px] px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-700 font-bold inline-flex items-center gap-1"
                        data-testid="speakers-in-progress-pill"
                        title={lang === "fr" ? "Ré-analyse en cours" : "Re-analysis in progress"}
                      >
                        <SpinnerGap size={10} className="animate-spin" />
                        {lang === "fr" ? "En cours" : "In progress"}
                      </span>
                    );
                  }
                  if (video?.speaker_validation === "validated") {
                    return (
                      <span
                        className="text-[10px] px-2 py-0.5 rounded-full border border-teal-300 bg-teal-50 text-teal-700 font-bold inline-flex items-center gap-1"
                        data-testid="speakers-validated-pill"
                        title={lang === "fr" ? "Identification validée" : "Identification validated"}
                      >
                        <CheckCircle size={10} weight="fill" />
                        {lang === "fr" ? "Validé" : "Validated"}
                      </span>
                    );
                  }
                  return null;
                })()}
              </h4>
              {canEdit && speakerData.detected_speakers?.length > 0 && (
                <Button
                  size="sm"
                  onClick={handleValidateSpeakers}
                  disabled={speakerSaving || !speakerData.detected_speakers?.length}
                  className="gap-1.5 text-xs"
                  data-testid="validate-speakers-btn"
                  title={
                    lang === "fr"
                      ? "Confirmer l'identification des locuteurs et propager dans la transcription"
                      : "Confirm speaker identification and propagate to transcript"
                  }
                >
                  {speakerSaving ? (
                    <SpinnerGap size={12} className="animate-spin" />
                  ) : (
                    <CheckCircle size={12} />
                  )}
                  {lang === "fr" ? "Valider l'identification" : "Confirm identification"}
                </Button>
              )}
              {/* Vincent B692 (Eric 2026-02-08) — Bouton "Annuler la validation".
                  Visible uniquement si une validation a déjà été faite. */}
              {canEdit && video?.speaker_validation === "validated" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    // B658 v2 (Eric 2026-02-17) — Migration window.confirm → confirmDialog
                    // (bloqué dans l'iframe Preview + centralise le style).
                    const ok = await confirmDialog(
                      lang === "fr"
                        ? "Annuler toute la validation d'identification ?\n\nCela va :\n• Supprimer tous les renommages manuels de locuteurs sur les segments\n• Restaurer les labels génériques (Speaker A/B/1/2...)\n• Vous permettre de refaire entièrement la validation\n\nLes noms enregistrés dans la banque de locuteurs sont conservés."
                        : "Cancel all identification validation?\n\nThis will:\n• Remove all manual speaker renamings on segments\n• Restore generic labels (Speaker A/B/1/2...)\n• Let you redo the validation from scratch\n\nSpeaker bank entries are preserved.",
                      { danger: true, confirmLabel: lang === "fr" ? "Annuler la validation" : "Reset validation" },
                    );
                    if (!ok) return;
                    try {
                      const resp = await axios.post(
                        `${api}/api/videos/${video.id}/speakers/reset-validation`,
                        {},
                        { withCredentials: true }
                      );
                      const n = resp.data?.segments_reset || 0;
                      toast.success(
                        lang === "fr"
                          ? `Validation annulée (${n} segments restaurés)`
                          : `Validation reset (${n} segments restored)`
                      );
                      if (typeof loadVideoData === "function") await loadVideoData();
                    } catch (err) {
                      toast.error(
                        (lang === "fr" ? "Erreur : " : "Error: ") +
                          (err?.response?.data?.detail || err.message)
                      );
                    }
                  }}
                  className="gap-1.5 text-xs border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="reset-speakers-validation-btn"
                  title={
                    lang === "fr"
                      ? "Annuler toute la validation d'identification et revenir aux labels génériques"
                      : "Cancel all identification and restore generic labels"
                  }
                >
                  <ArrowClockwise size={12} />
                  {lang === "fr" ? "Annuler la validation" : "Reset validation"}
                </Button>
              )}
            </div>
            {speakerData.detected_speakers?.length > 0 ? (
              <div className="space-y-2">
                {/* Vincent B716 fallback (Eric 2026-02-11) — Bannière discrète
                    quand AUCUN locuteur détecté n'est associé à un utilisateur
                    plateforme, pour rappeler à l'utilisateur qu'il peut
                    activer la messagerie 1-to-1 depuis /dashboard/speakers.
                    B729 (Eric 2026-02-13) — Concepteurs uniquement : un
                    consommateur ne peut pas accéder à /dashboard/speakers
                    ni y créer des associations. */}
                {canEdit && (() => {
                  const detectedMapped = speakerData.detected_speakers.map((spk) => {
                    const mappedName = speakerMapping[spk] || spk;
                    return speakerData.known_speakers?.find((ks) => ks.name === mappedName);
                  }).filter(Boolean);
                  const anyLinked = detectedMapped.some((s) => s?.associated_user);
                  const hasKnownSpeakers = detectedMapped.length > 0;
                  if (anyLinked || !hasKnownSpeakers) return null;
                  return (
                    <div
                      className="flex items-start gap-2 px-3 py-2 rounded-md bg-violet-50 border border-violet-200 text-[11px] text-violet-900"
                      data-testid="speakers-envelope-tip"
                    >
                      <span aria-hidden="true" className="flex-shrink-0 mt-0.5">💡</span>
                      <span className="flex-1">
                        {lang === "fr"
                          ? <>Associez ces locuteurs à des utilisateurs sur la page{" "}
                              <a
                                href="/dashboard/speakers"
                                className="underline font-semibold text-violet-700 hover:text-violet-900"
                                data-testid="speakers-envelope-tip-link"
                              >Locuteurs</a>
                              {" "}pour permettre l&apos;envoi de messages depuis cet onglet.</>
                          : <>Link these speakers to platform users on the{" "}
                              <a
                                href="/dashboard/speakers"
                                className="underline font-semibold text-violet-700 hover:text-violet-900"
                                data-testid="speakers-envelope-tip-link"
                              >Speakers</a>
                              {" "}page to enable direct messaging from this tab.</>}
                      </span>
                    </div>
                  );
                })()}
                {speakerData.detected_speakers.map((spk) => {
                  // Eric 2026-05-12 — Affichage de la photo du conseiller mappé
                  // (au lieu de l'icône Microphone violette générique).
                  const mappedName = speakerMapping[spk] || spk;
                  const mappedSpeaker = speakerData.known_speakers?.find(
                    (ks) => ks.name === mappedName
                  );
                  const hasPhoto =
                    mappedSpeaker &&
                    (mappedSpeaker.has_photo ||
                      mappedSpeaker.photo_base64 ||
                      mappedSpeaker.photo_mime);
                  // Vincent B625 (Eric 2026-07-30 SPEC STRICK PROD) — état
                  // validé = fond teal + bordure teal ; pending (défaut ou
                  // après re-diarisation) = zinc neutre.
                  // Vincent B625 renforcement (Eric 2026-07-31 SPEC STRICK PROD, 2e passe) —
                  // Border-2 + teal-100 pour une différenciation visuelle
                  // vraiment nette. Ajout d'une ombre teale pour attirer l'œil.
                  // Vincent B635 récurrence (Eric 2026-08-05 SPEC STRICK PROD) —
                  // Neutralise le style teal tant que le pipeline tourne
                  // (le back-end a remis pending mais le frontend peut afficher
                  // l'ancien state) pour éviter de laisser croire à l'utilisateur
                  // que l'identification EST validée alors qu'on est en train
                  // de la refaire.
                  const _pipelineActive = ["queued", "processing"].includes(video?.pipeline_status);
                  const isValidated = video?.speaker_validation === "validated" && !_pipelineActive;
                  // Vincent B716 (Eric 2026-02-11) — Utilisateur plateforme
                  // associé à ce locuteur. Si présent, on affiche une
                  // enveloppe cliquable à GAUCHE de la photo pour ouvrir
                  // (ou créer) une conversation individuelle. L'enveloppe
                  // est un bouton dédié (contrairement au clic sur toute
                  // la ligne, qui reste réservé à d'autres actions).
                  const linkedUser = mappedSpeaker?.associated_user;
                  return (
                    <div
                      key={spk}
                      className={`flex items-center gap-3 p-2.5 rounded-lg border-2 transition-colors ${
                        isValidated
                          ? "bg-teal-100 border-teal-500 shadow-sm shadow-teal-200"
                          : "bg-zinc-50 border-zinc-200"
                      }`}
                      data-testid={`speaker-row-${spk}`}
                      data-validated={isValidated ? "true" : "false"}
                    >
                      {linkedUser && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (typeof window !== "undefined") {
                              window.location.href = `/dashboard/agora?tab=conversations&dm=${encodeURIComponent(linkedUser.id)}`;
                            }
                          }}
                          className="p-1.5 rounded-full text-[#002FA7] hover:bg-[#002FA7]/10 transition-colors flex-shrink-0"
                          title={lang === "fr"
                            ? `Écrire à ${linkedUser.name}${linkedUser.email ? ` (${linkedUser.email})` : ""}`
                            : `Message ${linkedUser.name}`}
                          aria-label={`Message ${linkedUser.name}`}
                          data-testid={`speaker-envelope-btn-${spk}`}
                        >
                          <EnvelopeSimple size={18} weight="fill" />
                        </button>
                      )}
                      {hasPhoto ? (
                        <img
                          src={`${api}/api/speakers/${mappedSpeaker.id}/photo?t=${mappedSpeaker.photo_updated_at || ""}`}
                          alt={mappedName}
                          className={`w-9 h-9 rounded-full object-cover flex-shrink-0 transition-colors ${
                            isValidated ? "border-2 border-teal-600 ring-2 ring-teal-200" : "border border-zinc-200"
                          }`}
                          data-testid={`speaker-photo-${spk}`}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <div
                          className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                            isValidated
                              ? "bg-teal-200 border-2 border-teal-600 ring-2 ring-teal-200"
                              : "bg-violet-500/15 border border-transparent"
                          }`}
                          data-testid={`speaker-icon-${spk}`}
                        >
                          <Microphone
                            size={14}
                            className={isValidated ? "text-teal-800" : "text-violet-500"}
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-xs truncate font-semibold ${
                            isValidated ? "text-teal-900" : "text-zinc-500"
                          }`}
                          data-testid={`speaker-label-${spk}`}
                        >
                          {isValidated && <CheckCircle size={12} weight="fill" className="inline mr-1 -mt-0.5 text-teal-700" />}
                          {spk}
                        </p>
                        {/* Vincent XLSX 2026-07-16 Rows 491 + 492 (Eric 2026-07-16
                            SPEC STRICK) — Le menu déroulant "Locuteurs détectés"
                            listait auparavant TOUS les known_speakers de l'org.
                            Vincent veut :
                              #491 → retirer le menu déroulant devenu redondant
                                     (la liste à cocher ci-dessus tient ce rôle)
                              #492 → mais garder un moyen d'attribuer CHAQUE
                                     Speaker_X à un locuteur connu
                            Nouvelle solution : petit menu déroulant filtré,
                            n'affichant QUE les locuteurs COCHÉS dans la liste
                            à cocher (multi-sélection). Si aucun n'est coché,
                            on affiche un lien pour ouvrir la liste au-dessus. */}
                        {(() => {
                          const presentList = [...(speakerData.known_speakers || [])]
                            .filter((ks) => presentSpeakers && presentSpeakers.has(ks.name))
                            .sort((a, b) =>
                              String(a.name || "").localeCompare(String(b.name || ""), "fr", { sensitivity: "base" })
                            );
                          if (presentList.length === 0) {
                            // B729 (Eric 2026-02-13) — Message informatif
                            // pour concepteurs uniquement. Un consommateur
                            // ne peut rien faire de cette info, on la cache.
                            if (!canEdit) return null;
                            return (
                              <p className="text-[11px] text-amber-700 mt-1 italic" data-testid={`speaker-no-selection-${spk}`}>
                                {lang === "fr"
                                  ? "↑ Cochez d'abord les locuteurs présents dans la liste au-dessus pour pouvoir les attribuer."
                                  : "↑ Check present speakers in the list above first."}
                              </p>
                            );
                          }
                          return (
                            <select
                              value={speakerMapping[spk] || ""}
                              onChange={(e) =>
                                setSpeakerMapping((m) => ({ ...m, [spk]: e.target.value || spk }))
                              }
                              disabled={!canEdit}
                              className="mt-1 w-full text-sm border border-zinc-200 rounded px-2 py-1 bg-white disabled:bg-zinc-100 disabled:cursor-not-allowed"
                              data-testid={`speaker-map-${spk}`}
                            >
                              <option value="">
                                {lang === "fr" ? `— Attribuer ${spk} à… —` : `— Assign ${spk} to… —`}
                              </option>
                              {/* Vincent XLSX 2026-07-20 Row 492 (Eric SPEC STRICK) —
                                  Le menu proposait les mêmes options pour tous
                                  les Speaker_X sans indiquer si un locuteur
                                  était déjà attribué à un autre Speaker.
                                  Vincent veut « attribuer chaque Speaker à
                                  un locuteur DIFFÉRENT ». Amélioration : chaque
                                  option indique explicitement si elle est déjà
                                  utilisée par un autre Speaker (ex: « Diane
                                  (déjà attribué à Speaker 1) »). Le mapping
                                  reste techniquement autorisé (Vincent peut
                                  choisir de garder le doublon), mais visuellement
                                  averti pour éviter les erreurs. */}
                              {presentList.map((ks) => {
                                const usedByOther = Object.entries(speakerMapping).find(
                                  ([otherSpk, val]) => otherSpk !== spk && val === ks.name
                                );
                                const suffix = usedByOther
                                  ? (lang === "fr"
                                      ? ` — déjà attribué à ${usedByOther[0]}`
                                      : ` — already assigned to ${usedByOther[0]}`)
                                  : "";
                                return (
                                  <option key={ks.id} value={ks.name}>
                                    {`${ks.name}${ks.role ? ` (${ks.role})` : ""}${suffix}`}
                                  </option>
                                );
                              })}
                              <option value="__unknown__">
                                {lang === "fr" ? "Locuteur inconnu — à identifier" : "Unknown speaker"}
                              </option>
                            </select>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 text-zinc-400">
                <Microphone size={28} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">
                  {`Aucun locuteur détecté. Cliquez "Ré-analyser" pour lancer l'identification.`}
                </p>
              </div>
            )}
          </div>

          {/* Quick: Assign all to single speaker — Vincent #355 (Eric 2026-06-22)
              masqué pour non-éditeurs (lecture seule). */}
          {canEdit && speakerData.detected_speakers?.length > 1 && (
            <div
              className="p-3 bg-amber-50 border border-amber-200 rounded-lg"
              data-testid="single-speaker-section"
            >
              <p className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
                <SpeakerHigh size={14} />
                {lang === "fr"
                  ? "Un seul locuteur dans cette vidéo ?"
                  : "Only one speaker in this video?"}
              </p>
              <p className="text-[11px] text-amber-700 mb-2">
                {lang === "fr"
                  ? "Si la diarisation a détecté plusieurs locuteurs par erreur, assignez tous les segments à une seule personne :"
                  : "If diarization incorrectly detected multiple speakers, assign all segments to a single person:"}
              </p>
              <div className="flex gap-2">
                <select
                  id="single-speaker-select"
                  className="flex-1 text-sm border border-amber-300 rounded px-2 py-1.5 bg-white"
                  data-testid="single-speaker-select"
                  defaultValue=""
                >
                  <option value="" disabled>
                    {lang === "fr" ? "— Choisir le locuteur —" : "— Choose speaker —"}
                  </option>
                  {/* Vincent #405 (Eric 2026-07-04 SPEC STRICK) — Tri A-Z */}
                  {[...(speakerData.known_speakers || [])]
                    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "fr", { sensitivity: "base" }))
                    .map((ks) => (
                    <option key={ks.id} value={ks.name}>
                      {`${ks.name}${ks.role ? ` (${ks.role})` : ""}`}
                    </option>
                  ))}
                </select>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1 text-xs border-amber-400 text-amber-800 hover:bg-amber-100"
                  data-testid="assign-single-speaker-btn"
                  onClick={() => {
                    const sel = document.getElementById("single-speaker-select");
                    const name = sel?.value;
                    if (!name) return;
                    const newMapping = {};
                    speakerData.detected_speakers.forEach((s) => {
                      newMapping[s] = name;
                    });
                    setSpeakerMapping(newMapping);
                  }}
                >
                  {lang === "fr" ? "Appliquer" : "Apply"}
                </Button>
              </div>
            </div>
          )}

          {/* Validation status */}
          {/* Vincent B635 récurrence (Eric 2026-08-05 SPEC STRICK PROD) —
              Masque le bandeau « Locuteurs validés » tant que le pipeline
              tourne : la ré-analyse va reset le state, on ne veut pas
              laisser croire à l'utilisateur que la validation est encore
              en vigueur pendant la re-diarisation. */}
          {speakerData.validation_status === "validated"
            && !["queued", "processing"].includes(video?.pipeline_status) && (
            <div className="flex items-center gap-2 p-2 bg-teal-50 border border-teal-200 rounded-lg text-teal-700 text-xs" data-testid="speakers-validated-banner">
              <CheckCircle size={14} weight="fill" />
              Locuteurs validés
            </div>
          )}

          {/* Vincent B620 (Eric 2026-07-30 SPEC STRICK) — Ancien bloc Actions
              retiré : « Valider l'identification » est désormais à droite du
              titre « Locuteurs détectés (N) », et « Ré-analyser l'audio » est
              supprimé (ne faisait pas ce qu'il fallait). La relance de
              diarisation reste accessible via « Corriger la vidéo » plus bas. */}

          {/* Vincent B890 (Eric 2026-02-14 SPEC STRICK PROD) — Les rectangles
              « Corriger la vidéo » et « Debug fondateur (ticket #500) » ont
              été DÉPLACÉS directement sous le player vidéo (via le composant
              VideoActionsCards dans VideoDetailPage), pour rester visibles
              quel que soit l'onglet du panneau de droite. Voir CHANGELOG
              B890 pour la rationalisation. Ce panneau ne contient plus que
              les blocs liés strictement aux LOCUTEURS. */}

          {/* Known speakers link — Vincent B723 (Eric 2026-02-12) :
              visible UNIQUEMENT aux utilisateurs autorisés à concevoir/corriger
              des formations (canEdit=true). Les autres ne peuvent rien en faire. */}
          {canEdit && (
          <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg text-xs text-violet-700" data-testid="known-speakers-hint">
            <p className="font-semibold mb-1">Profils de locuteurs</p>
            <p>
              {`Pour améliorer l'identification, ajoutez des profils avec photos dans `}
              <a href="/dashboard/speakers" className="underline font-medium">
                la page Locuteurs
              </a>
              .
            </p>
            <p className="mt-1">
              Profils connus: <strong>{speakerData.known_speakers?.length || 0}</strong> (
              {speakerData.known_speakers?.filter(
                (s) => s.has_photo || s.photo_base64 || s.photo_mime
              ).length || 0}{" "}
              avec photo)
            </p>
          </div>
          )}
        </>
      )}
    </div>
  );
};

export default SpeakersPanel;
